import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { combineLatest, from, iif, of } from 'rxjs';
import { catchError, concatMap, first, map, mergeMap, switchMap } from 'rxjs/operators';

import { TicketAverange } from '../../private/shared/services/ticket-averange.service';
import { IIntervalSave } from '../../shared/enums/UpdateInfo';
import { CloudFunctionsProgrammes, GETS, NativeStorageNamesV2 } from '../../shared/models/Constants';
import {
    IDatesAwarded,
    IGamificacionCuotas,
    IGamificacionResumen,
    ProgramaCDGTenderos
} from '../../shared/models/ProgramaCDGTenderos.interface';
import { TicketPromedio } from '../../shared/models/TicketPromedio';
import { SharedServiceService } from '../../shared/services/shared-service.service';
import {
    completeLoadingProgram,
    errorGetAverageTicketCDG,
    errorGetCDGProgam,
    errorGetGamificacionAutoevaluacion,
    errorGetGamificacionCuotas,
    errorGetGamificacionResumen,
    getAverageTicketCDG,
    getGamificacionAutoevaluacion,
    getGamificacionCuotas,
    getGamificacionResumen,
    getPointsStorageCDG,
    getProgamCDG,
    setPointsStorageCDG,
    successGetAverageTicketCDG,
    successGetCDGProgam,
    successGetGamificacionAutoevaluacion,
    successGetGamificacionCuotas,
    successGetGamificacionResumen
} from '../actions';
import { AppState } from '../app-reducers';
import { StorageData } from './../../shared/services/utilities/storage-data.class';



@Injectable()
export class CDGProgramEffets {

    private storageBodegaNud = NativeStorageNamesV2.BodegaNud
    private storageProgramaTenderos = NativeStorageNamesV2.ProgramaTenderos
    private storageTicketPromedio = NativeStorageNamesV2.TicketPromedio
    private storageFechaPremiados = NativeStorageNamesV2.UltimaFechaConsultPremiados
    private storagePuntosPremiados = NativeStorageNamesV2.PuntosPremiados

    private cfPrograma = CloudFunctionsProgrammes

    constructor(
        private actions$: Actions
        , private sharedS: SharedServiceService
        , private ticketS: TicketAverange
        , private store: Store<AppState>
    ) { }

    public getCDGProgram$ = createEffect(
        () => this.actions$.pipe(
            ofType(getProgamCDG),
            switchMap((actions: { api: string }) =>
                from(StorageData.getDataStorage(this.storageBodegaNud))
                    .pipe(map((dataUser: Array<any>) => {
                        const programFunction = this.cfPrograma.find(x => x.ID_Programa === dataUser[2]).store
                        return { dataUser, actions, programFunction }
                    }))
            ),
            switchMap((dataStorageStoreProgram: { dataUser: Array<any>, programFunction: string, actions: { api: string } }) =>
                this.dataUser(dataStorageStoreProgram)
            )
        )
    )

    public getCDGAverageTicket$ = createEffect(
        () => this.actions$.pipe(
            ofType(getAverageTicketCDG),
            switchMap((actions: { programData: ProgramaCDGTenderos[] }) =>
                from(this.ticketS.CalculaTicketPromedioV2())
                    .pipe(
                        mergeMap((averageTicket: number) =>
                            iif(() => averageTicket !== null, of(averageTicket), this.dataAverageTicket(actions.programData, averageTicket))
                        ),
                        map((averageTicket: number) => successGetAverageTicketCDG({ data: { averageTicket } })),
                        catchError((err: Error) => of(errorGetAverageTicketCDG({ payload: err })))
                    )
            )
        )
    )

    public getPointsStorageCDG$ = createEffect(
        () => this.actions$.pipe(
            ofType(getPointsStorageCDG),
            switchMap(() => combineLatest([from(StorageData.getDataStorage(this.storagePuntosPremiados)), from(StorageData.getDataStorage(this.storageFechaPremiados))])),
            map(([points, lastDate]) => ({ points, lastDate: lastDate === null ? '' : moment(lastDate).locale('es').format('DD [de] MMMM, YYYY · HH:mm') })),
            map(points => setPointsStorageCDG(points)),
        )
    )


    private dataUser = (dataStorageStore: { dataUser: Array<any>, programFunction: string, actions: { api: string } }) => {
        return this.sharedS.executeFunctionDataUser(dataStorageStore.actions.api, { clodFucntions: dataStorageStore.programFunction }).pipe(
            map((respProgram: ProgramaCDGTenderos[]) => {
                StorageData.setDataStorage(this.storageProgramaTenderos, respProgram)
                return this.successUserProgram(respProgram)
            }),
            catchError((err: Error) => of(errorGetCDGProgam({ payload: err })))
        )
    }

    private successUserProgram = (resp: ProgramaCDGTenderos[]) => {
        resp.length > 0
            ? this.store.dispatch(getAverageTicketCDG({ programData: resp }))
            : this.store.dispatch(completeLoadingProgram())

        return successGetCDGProgam({ data: resp })
    }

    private dataAverageTicket = (programData: ProgramaCDGTenderos[], quantityAverage: number) => {
        const obsAverageTicket$ = quantityAverage
            ? of(quantityAverage)
            : this.ticketS.nativeError(programData[0].ID_Bodega, programData[0].NUD, programData[0].Year, programData[0].Month)
                .pipe(
                    map((respTicket: TicketPromedio[]) => {
                        StorageData.setDataStorage(this.storageTicketPromedio, respTicket)
                        return respTicket[0].TicketPromedio
                    })
                )

        return obsAverageTicket$
    }

    private validateDatesSchedules = async (actionsDates: { datesAwarded: IDatesAwarded, lastDate: string }): Promise<any> => {
        const currentDate = moment()
        let datesRangeValid = false
        let schedulesValid = false

        const startDate = moment(actionsDates.datesAwarded.fecha_inicial_premiados).format('YYYY-MM-DD')
        const endDate = moment(actionsDates.datesAwarded.fecha_final_premiados).format('YYYY-MM-DD')

        datesRangeValid = currentDate.isBetween(startDate, endDate)

        const currentDatesSchedules = actionsDates.datesAwarded.horarios_consulta_premiados.map(horario =>
            moment(currentDate.format('YYYY-MM-DD' + ' ' + horario)).format('YYYY-MM-DD HH:mm:ss')
        )

        let filteredSchedule = []
        if (actionsDates.lastDate === null) {
            filteredSchedule = currentDatesSchedules.filter(scheduleDate => currentDate.isSameOrAfter(moment(scheduleDate)))
        } else {
            const filteredStorage = currentDatesSchedules.filter(scheduleDate => moment(actionsDates.lastDate).isBefore(moment(scheduleDate)))
            filteredSchedule = filteredStorage.filter(scheduleDate => currentDate.isSameOrAfter(moment(scheduleDate)))
        }

        schedulesValid = filteredSchedule.length > 0

        if (schedulesValid) {
            await StorageData.setDataStorage(this.storageFechaPremiados, currentDate.format('YYYY-MM-DD HH:mm:ss'))
        }

        if (!datesRangeValid) {
            await StorageData.removeDataStorage(this.storagePuntosPremiados)
            await StorageData.removeDataStorage(this.storageFechaPremiados)
        }

        this.store.dispatch(getPointsStorageCDG())

        return datesRangeValid && schedulesValid
    }

}

@Injectable()
export class GamificacionEffets {

    constructor(
        private actions$: Actions
        , private httpS: SharedServiceService
        , private translate: TranslateService
    ) { }

    public getGamificacionResumen$ = createEffect(
        () => this.actions$.pipe(
            ofType(getGamificacionResumen),
            switchMap(
                () => from(StorageData.getDataStorage(NativeStorageNamesV2.Resumen))
                    .pipe(
                        concatMap((storage: ISaveResumen) => this.translate.get('Miscellaneous.updateinterval5').pipe(first(), map((UpdateInterval5: IIntervalSave) => ({ storage, UpdateInterval5 })))),
                        concatMap(({ storage, UpdateInterval5 }) => this.httpS.getStorage(storage, NativeStorageNamesV2.Resumen, UpdateInterval5, 'executeFunctionDataUser', GETS.Resumen, null))
                    )
            ),
            map(resp => successGetGamificacionResumen(resp)),
            catchError((err: Error) => of(errorGetGamificacionResumen({ payload: err }))),
        )
    );

    public getGamificacionAutoevaluacion$ = createEffect(
        () => this.actions$.pipe(
            ofType(getGamificacionAutoevaluacion),
            switchMap(() => this.httpS.executeFunctionDataUser(GETS.Autoevaluacion, {})),
            map(resp => successGetGamificacionAutoevaluacion(resp)),
            catchError((err: Error) => of(errorGetGamificacionAutoevaluacion({ payload: err }))),
        )
    );

    public getGamificacionCuotas$ = createEffect(
        () => this.actions$.pipe(
            ofType(getGamificacionCuotas),
            switchMap(
                () => from(StorageData.getDataStorage(NativeStorageNamesV2.Cuotas))
                    .pipe(
                        concatMap((storage: ISaveCuotas) => this.translate.get('Miscellaneous.updateinterval5').pipe(first(), map((UpdateInterval5: IIntervalSave) => ({ storage, UpdateInterval5 })))),
                        concatMap(({ storage, UpdateInterval5 }) => this.httpS.getStorage(storage, NativeStorageNamesV2.Cuotas, UpdateInterval5, 'executeFunctionDataUser', GETS.Cuotas, null))
                    )
            ),
            map(resp => successGetGamificacionCuotas(resp)),
            catchError((err: Error) => of(errorGetGamificacionCuotas({ payload: err }))),
        )
    );
}

interface ISaveResumen {
    fecha: string;
    data: IGamificacionResumen;
}

interface ISaveCuotas {
    fecha: string;
    data: IGamificacionCuotas;
}
